import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Phone, CheckCircle2, Hammer } from "lucide-react";
import { COMPANY, SERVICES, SERVICE_PAGES, CITY_PAGES } from "@/lib/site-data";
import LogoEmblem from "@/components/LogoEmblem";
import CountUp from "@/components/CountUp";
import { useSeo, orgSchema } from "@/lib/useSeo";

const ease = [0.22, 1, 0.36, 1];

export default function ServicePage() {
  const { slug } = useParams();
  const page = SERVICE_PAGES.find((s) => s.slug === slug);
  const service = page ? SERVICES.find((s) => s.id === page.serviceId) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const faqs =
    page && service
      ? [
          {
            q: `${service.title} : combien ça coûte ?`,
            a: "Chaque toiture est unique : surface, matériau, accessibilité et état du support font le prix. Notre diagnostic est gratuit et le devis, remis sous 48 h, est ferme et détaillé poste par poste.",
          },
          {
            q: `Quels sont vos délais pour ${service.title.toLowerCase()} ?`,
            a: "Les urgences sont traitées sous 2 heures, 7 jours sur 7. Les chantiers planifiés démarrent sous 2 à 4 semaines selon la météo et l'approvisionnement, avec un calendrier annoncé dès le devis.",
          },
          {
            q: "Vos travaux sont-ils garantis ?",
            a: "Oui : tous nos chantiers sont couverts par la garantie décennale et la responsabilité civile professionnelle. Les attestations vous sont remises avant le début des travaux.",
          },
        ]
      : [];

  useSeo({
    title: page ? `${page.h1} — NB Couverture` : undefined,
    description: page?.meta,
    canonical: page ? `/services/${page.slug}` : undefined,
    schemas: page
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://nb-couverture-bordeaux.fr/" },
              { "@type": "ListItem", position: 2, name: "Prestations", item: "https://nb-couverture-bordeaux.fr/#services" },
              { "@type": "ListItem", position: 3, name: page.h1, item: `https://nb-couverture-bordeaux.fr/services/${page.slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.h1,
            description: page.meta,
            provider: orgSchema,
            areaServed: CITY_PAGES.map((c) => c.name),
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : [],
  });

  if (!page || !service) return <Navigate to="/" replace />;
  const Icon = service.icon || Hammer;

  return (
    <div className="min-h-screen bg-[#0D0F12] text-slate-200 antialiased">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0D0F12]/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="service-logo" className="flex items-center gap-3">
            <LogoEmblem size={40} />
            <span className="font-serif text-sm font-semibold text-white">NB Couverture</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="service-back-home"
              className="hidden items-center gap-2 text-xs font-semibold text-slate-300 transition-colors hover:text-terra sm:inline-flex"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Retour à l'accueil
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="service-phone-cta"
              className="inline-flex items-center gap-2 rounded-full bg-terra px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-terra-hover"
            >
              <Phone className="h-3.5 w-3.5" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-terra">
            <Icon className="h-3.5 w-3.5" />
            NB Couverture — Bordeaux & Gironde
          </p>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-tight text-white md:text-6xl">{page.h1}</h1>
          <p data-testid="service-intro" className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            {page.intro}
          </p>
          <ul className="mt-6 flex flex-wrap gap-3">
            {page.points.map((p) => (
              <li
                key={p}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-slate-200"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-terra" />
                {p}
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="/#devis"
              data-testid="service-devis-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-terra px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terra-hover"
            >
              Devis gratuit
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-3 rounded-full border border-white/15 px-7 py-4 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:border-terra/60 hover:text-white"
            >
              <Phone className="h-4 w-4 text-terra" />
              {COMPANY.phone}
            </a>
          </div>

          <div className="mt-12 grid max-w-2xl grid-cols-3 gap-6 rounded border border-white/10 bg-black/50 px-5 py-6 backdrop-blur-md md:px-8">
            {[
              { value: "25+", label: "années de métier" },
              { value: "596", label: "travaux effectués" },
              { value: "4,9/5", label: "avis clients" },
            ].map((s) => (
              <div key={s.label} data-testid={`service-stat-${s.label.replace(/\s/g, "-")}`}>
                <p className="font-serif text-3xl font-bold text-white md:text-4xl">
                  <CountUp value={s.value} />
                </p>
                <p className="mt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-white md:text-xs">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease }}
          className="mt-16 max-w-3xl"
        >
          {page.body.map((p, i) => (
            <p key={i} className="mt-5 text-sm leading-relaxed text-slate-300 md:text-base">
              {p}
            </p>
          ))}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease }}
          data-testid="service-faq"
          className="mt-16"
        >
          <h2 className="font-serif text-2xl font-semibold text-white md:text-3xl">Questions fréquentes</h2>
          <div className="mt-8 grid gap-4">
            {faqs.map((f) => (
              <div key={f.q} className="border border-white/5 bg-[#14171D] p-6">
                <h3 className="font-serif text-base text-slate-100 md:text-lg">{f.q}</h3>
                <p className="mt-2.5 text-xs leading-relaxed text-slate-400 md:text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <section className="mt-16 border-t border-white/5 pt-10">
          <h2 className="font-serif text-xl font-semibold text-white">
            {service.title} — partout en Gironde
          </h2>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2.5">
            {CITY_PAGES.map((c) => (
              <Link
                key={c.slug}
                to={`/couvreur/${c.slug}`}
                data-testid={`service-city-link-${c.slug}`}
                className="text-xs text-slate-400 underline-offset-4 transition-colors hover:text-terra hover:underline"
              >
                Couvreur à {c.name}
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2.5 border-t border-white/5 pt-6">
            {SERVICE_PAGES.filter((s) => s.slug !== page.slug).map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-link-${s.slug}`}
                className="text-xs text-slate-400 underline-offset-4 transition-colors hover:text-terra hover:underline"
              >
                {SERVICES.find((sv) => sv.id === s.serviceId)?.title}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8">
        <p className="mx-auto max-w-6xl px-6 text-xs text-slate-500">
          {COMPANY.name} — {COMPANY.address} · {COMPANY.phone} · Garantie décennale ·{" "}
          <Link to="/" className="text-slate-400 underline-offset-4 hover:text-terra hover:underline">
            Accueil
          </Link>
        </p>
      </footer>
    </div>
  );
}
